/* eslint-disable vue/max-len */
// Global application constants

export const appName = 'PROJEX'
export const appVersion = import.meta.env.VITE_APP_VERSION
export const appUrl = getBaseUrl()
export const sentryDsn = 'https://2c0b8d0c0f1346feafb066778fec1028@o575831.ingest.sentry.io/4503937348796416'
export const capabilitiesUrl = 'https://nzweb01.corp.pbwan.net/capabilities/'
export const resourcePlannerUrl = getResourcePlannerUrl()
export const nzProjexFutureReadyLink = 'https://canopy.wsp.com/sites/future-ready-nz/SitePageModern/180196/key-trends-and-research-nz'
export const defaultMapMarkerZoom = 10
export const defaultBurdenRate = 2.50
export const cappedBurdenRate = 2.70

function getBaseUrl() {
    switch (import.meta.env.MODE) {
        case 'uat': return 'https://uat.projex.wsp.com'
        case 'production': return 'https://projex.wsp.com'
        default: return import.meta.env.VITE_API_URL // Let developer decide what url they want.
    }
}

function getResourcePlannerUrl() {
    switch (import.meta.env.MODE) {
        case 'uat': return 'https://appsuat.wspanz.com/resource-planner'
        case 'production': return 'https://apps.wspanz.com/resource-planner'
        default: return import.meta.env.VITE_RESOURCE_PLANNER_URL
    }
}

export const dev = {
    name: 'WSP NZ Software Development Team',
    email: 'dev@wsp.com',
}

export const emails = {
    auFinance: 'AU.financecompliance@wsp.com',
}

// Special error indicating user aborted some action - our sentry package is configured to ignore these promise rejections
export const userAbortError = 'User abort'

export const daysOfMonth = Array(31).fill().map((_, i) => ({ id: i + 1, value: i + 1 }))

export const isProductionBuild = import.meta.env.PROD // Both UAT (QA) and PROD are production builds
export const isProduction = import.meta.env.MODE === 'production'
export const isUAT = import.meta.env.MODE === 'uat' || import.meta.env.MODE === 'qa' || import.meta.env.MODE === 'appsuat'
export const isDev = import.meta.env.MODE === 'development' || import.meta.env.MODE === 'local'

export const lessonLearnedPowerAppUrl = isUAT
    ? 'https://apps.powerapps.com/play/e/7f28e1bc-3de3-4793-9375-5a15d6112461/a/127d1e2b-b3ee-4408-a937-789008c06b21?tenantId=3d234255-e20f-4205-88a5-9658a402999b&hint=d50c6aff-d26d-4236-aa07-069edb5b019a&sourcetime=1701305319174'
    : 'https://apps.powerapps.com/play/e/def37f49-28de-4705-a9fe-2e1918ee9bfa/a/7a58c12f-e784-41c7-827d-56a9e8571183?tenantId=3d234255-e20f-4205-88a5-9658a402999b&hint=4daa666d-1f7b-44a8-8f64-c5d386c0ab65&sourcetime=1701394373364'

// Should match our morphmap defined in AppServiceProvider.php
export const morphTypes = {
    ACCREDITED_OVERSEER: 'accredited_overseer',
    CLIENT_FEEDBACK: 'client_feedback',
    CLIENT_FEEDBACK_SCHEDULE: 'client_feedback_schedule',
    COMMUNICATION_PLAN: 'communication_plan',
    EXECUTE_AS_PLANNED: 'execute_as_planned',
    LESSON_LEARNED: 'lesson_learned',
    MEDIA: 'media',
    MPR: 'mpr',
    OTHER_KEY_ROLE: 'other_key_role',
    OTHER_STAKEHOLDER: 'other_stakeholder',
    PLAN: 'plan',
    PROJECT_DELIVERABLE: 'project_deliverable',
    PROJECT_EXECUTION_PLAN: 'project_execution_plan',
    PROJECT_REVIEW: 'project_review',
    PROJECT_SHEET: 'project_sheet',
    RISK_AND_OPPORTUNITY: 'risk_and_opportunity',
    RISK_REGISTER: 'risk_register',
    SCOPE_CHANGE: 'scope_change',
    WORK_AT_RISK_HISTORY: 'work_at_risk_history',
    SHEQ_INSPECTION: 'sheq_inspection',
    TASK_PLAN: 'task_plan',
    USER: 'user',
    AUDIT_AND_INSPECTION: 'audit_and_inspection',
}

export const securityLevels = {
    SECURITY_LEVEL_INTERNAL: 1,
    SECURITY_LEVEL_PRIVATE: 2,
    SECURITY_LEVEL_CONFIDENTIAL: 3,
}

export const dataSourceRegions = {
    AU: 1,
    CA: 2,
    A49: 3,
    US: 4,
    UK: 5,
    IE: 6,
    NZ: 7,
    ZAF: 8,
    GHA: 9,
    DRC: 10,
    MOZ: 11,
    MRT: 12,
    KSA: 13,
    OMN: 14,
    QAT: 15,
    KWT: 16,
    UAE: 17,
    IDN: 18,
    MANILA: 19,
}

export const userStatus = {
    STATUS_ACTIVE: 50,
    STATUS_INACTIVE: 51,
    STATUS_TERMINATED: 52,
    STATUS_UNKNOWN: 53,
}

export const userRoles = {
    ROLE_CEO_OR_COO: 1901,
    ROLE_M1: 1902,
    ROLE_M2: 1903,
    ROLE_M3: 1904,
    ROLE_M4: 1905,
    ROLE_M5: 1906,
    ROLE_CL: 1907,
    ROLE_PM: 1908,
    ROLE_PD: 1909,
    ROLE_CFO: 1910,
    ROLE_DPD: 1911,
    ROLE_MAAU_PA_GNL: 1912,
    ROLE_MAGBS_PFA: 1913,
}

export const otherKeyRoles = {
    ASSISTANT_PROJECT_MANAGER: 700,
    AUDITOR: 701,
    BIM_COORDINATOR: 702,
    CLIENT_RELATIONSHIP_MANAGER: 703,
    CONTRACT_ADMINISTRATOR: 704,
    DESIGN_MANAGER: 705,
    DIGITAL_ENGINEERING_LEAD: 706,
    DISCIPLINE_LEAD: 707,
    DOCUMENT_CONTROLLER: 708,
    PROJECT_CONTROLLER: 709,
    PROJECT_PARTNER: 710,
    SECTION_EXECUTIVE: 711,
    SITE_SUPERVISOR: 712,
    STEERING_COMMITTEE_MEMBER: 713,
    OTHER: 714,
    ARCHITECT: 715,
    PRINCIPAL_CONTRACTOR: 716,
    PROGRAM_DIRECTOR: 717,
    PROJECT_MANAGER_MENTOR: 720,
    BILLER: 722,
    LEGAL_COUNSEL: 724,
    RISK_MANAGER: 725,
    REVIEWER_VERIFIER: 726,
}

export const planStatus = {
    STATUS_ACTIVE: 60,
    STATUS_CLOSED: 61,
    STATUS_ON_HOLD: 62,
    STATUS_UNKNOWN: 63,
}

export const projectReviewType = {
    TYPE_PROJECT_REVIEW: 600,
    TYPE_PROJECT_PARTNER_REVIEW: 601,
    TYPE_INTERNAL_AUDIT: 602,
    TYPE_CERTIFICATION_AUDIT: 603,
    TYPE_FINANCIAL_AUDIT: 604,
    TYPE_OTHER: 605,
    TYPE_MONTHLY_PROJECT_REVIEW: 606,
}

export const mprRolloverType = {
    ROLLOVER_TYPE_DEFAULT: 70,
    ROLLOVER_TYPE_DAY: 71,
    ROLLOVER_TYPE_DAY_AND_WEEK: 72,
}

export const mprStatus = {
    STATUS_READY_TO_START: 1100,
    STATUS_DRAFT: 1101,
    STATUS_PM_SUBMITTED: 1102,
    STATUS_PD_SUBMITTED: 1103,
    STATUS_COMPLETE: 1104,
    STATUS_HOLD: 1105,
    STATUS_BL_SUBMITTED: 1106,
    STATUS_CL_SUBMITTED: 1107,
}

export const mprOnHoldReason = {
    REASON_SMALL_PROJECT: 650,
    REASON_WARRANTY_LIABILITY: 654,
    REASON_OTHER: 655,
}

export const mprOnHoldMonth = {
    MONTH_1: 690,
    MONTH_2: 691,
    MONTH_3: 692,
    MONTH_12: 693,
}

export const taskStatus = {
    STATUS_SCHEDULED: 90,
    STATUS_TENATIVE: 91,
    STATUS_IN_PROGRESS: 92,
    STATUS_COMPLETE: 93,
    STATUS_CANCELLED: 94,
}

export const changeRegisterStatus = {
    STATUS_DRAFT: 289,
    STATUS_APPROVED: 290,
    STATUS_REJECTED: 291,
    STATUS_IN_PROGRESS: 292,
    STATUS_CANCELED: 293,
    STATUS_COMPLETED: 294,
}

export const projectDeliverableStatus = {
    STATUS_NOT_STARTED: 270,
    STATUS_IN_PROGRESS: 271,
    STATUS_COMPLETED: 272,
}

export const ragStatus = {
    RED: 1800,
    AMBER: 1801,
    GREEN: 1802,
}

export const kpis = {
    MPR_SNAPSHOT_FINANCIAL_IMPACT_NET_REVENUE_PERCENT: 21,
    MPR_SNAPSHOT_FINANCIAL_IMPACT_NET_REVENUE_AMOUNT: 22,
    MPR_SNAPSHOT_AR_AGING: 23,
    MPR_SNAPSHOT_WIP_AGING: 24,
}

export const clientFeedbackExemptionStatus = {
    SUBMITTED: 1600,
    APPROVED: 1601,
    DECLINED: 1602,
}

export const yesNoOptions = {
    YES: 2850,
    NO: 2851,
    NOT_APPLICABLE: 2852,
}

export const signOffTypes = {
    TYPE_PLAN_PROJECT_APPROVAL_PD: 145,
    TYPE_PLAN_WORK_AT_RISK_APPROVAL_PD: 146,
    TYPE_PLAN_CLOSURE_PD: 147,
    TYPE_PLAN_CLOSURE_PM: 148,
    TYPE_PLAN_PLANNING_PM: 150,
    TYPE_PLAN_INITIATION_PM: 151,
    TYPE_MPR_PROJECT_MANAGER: 152,
    TYPE_MPR_PROJECT_DIRECTOR: 153,
    TYPE_MPR_COMMERCIAL_LEADER: 154,
    TYPE_MPR_BUSINESS_LEADER: 155,
}

export const taskPlanTypes = {
    TYPE_HEALTH_AND_SAFETY: 180,
    TYPE_RISK: 181,
    TYPE_SAFETY_IN_DESIGN: 182,
    TYPE_ENVIRONMENTAL: 183,
    TYPE_SUSTAINABILITY: 184,
    TYPE_OTHER: 185,
    TYPE_HSE_RISK_REGISTER: 186,
}

export const riskRegisterLikelihoods = {
    LIKELIHOOD_1: 820,
    LIKELIHOOD_2: 821,
    LIKELIHOOD_3: 822,
    LIKELIHOOD_4: 823,
    LIKELIHOOD_5: 824,
}

export const pmLevelExceptions = {
    PM_LEVEL_ACCEPTABLE_NO: 3900,
    PM_LEVEL_ACCEPTABLE_YES: 3901,
    PM_LEVEL_ACCEPTABLE_YES_APPROVED: 3902,
}

export const likelihoodValues = {
    [riskRegisterLikelihoods.LIKELIHOOD_1]: 1,
    [riskRegisterLikelihoods.LIKELIHOOD_2]: 2,
    [riskRegisterLikelihoods.LIKELIHOOD_3]: 3,
    [riskRegisterLikelihoods.LIKELIHOOD_4]: 4,
    [riskRegisterLikelihoods.LIKELIHOOD_5]: 5,
}

export const riskRegisterConsequences = {
    CONSEQUENCE_1: 810,
    CONSEQUENCE_2: 811,
    CONSEQUENCE_3: 812,
    CONSEQUENCE_4: 813,
    CONSEQUENCE_5: 814,
}

export const consequenceValues = {
    [riskRegisterConsequences.CONSEQUENCE_1]: 1,
    [riskRegisterConsequences.CONSEQUENCE_2]: 2,
    [riskRegisterConsequences.CONSEQUENCE_3]: 3,
    [riskRegisterConsequences.CONSEQUENCE_4]: 4,
    [riskRegisterConsequences.CONSEQUENCE_5]: 5,
}

export const riskRegisterRatings = {
    RATING_LOW: 800,
    RATING_MEDIUM: 801,
    RATING_HIGH: 802,
    RATING_CRITICAL: 803,
}

export const riskRatingValues = {
    [riskRegisterRatings.RATING_LOW]: 0,
    [riskRegisterRatings.RATING_MEDIUM]: 6,
    [riskRegisterRatings.RATING_HIGH]: 7,
    [riskRegisterRatings.RATING_CRITICAL]: 9,
}

export const executeAsPlannedTypes = {
    TYPE_CLIENT_EXPERIENCE: 250,
    TYPE_HSE_DESIGN_AND_OTHER_RISKS: 251,
    TYPE_RESOURCES_AND_PROCUREMENT: 252,
    TYPE_OTHER_STAKEHOLDER_INTERFACES: 253,
    TYPE_INFORMATION_AND_COMMUNICATION: 254,
    TYPE_QUALITY: 255,
    TYPE_SCOPE_SCHEDULE_AND_COST: 256,
}

export const sheqInspectionTypes = {
    TYPE_OTHER: 905,
}

export const scopeChangeRegisterStatuses = {
    PREDICTED_JOB_GROWTH: 193,
}

export const budgetUpdateStatuses = {
    STATUS_IN_PROGRESS: 1230,
    STATUS_APPROVED: 1231,
    STATUS_DECLINED: 1232,
    STATUS_UNDER_REVIEW: 1235,
    STATUS_PA_REVIEW: 1234,
}
export const budgetUpdateSource = {
    SOURCE_PROJEX: 2400,
    SOURCE_LEGACY: 2401,
    SOURCE_GDW: 2402,
}

export const clientFeedbackStatuses = {
    STATUS_PENDING: 470,
    STATUS_RECEIVED: 471,
}

export const clientFeedbackTypes = {
    TYPE_PULSE_VIA_PDP: 501,
    TYPE_DETAILED_VIA_PDP: 502,
    TYPE_VERBAL: 503,
    TYPE_EMAIL_LETTER: 504,
    TYPE_OTHER: 505,
    TYPE_ACTION_ONLY: 506,
}

export const clientFeedbackFormTypes = {
    FORM_TYPE_PULSE: 551,
    FORM_TYPE_DETAILED: 552,
}

export const clientFeedbackFormTypeOptions = [
    {
        id: clientFeedbackFormTypes.FORM_TYPE_PULSE,
        name: 'client_feedback_form_type_pulse',
    },
    {
        id: clientFeedbackFormTypes.FORM_TYPE_DETAILED,
        name: 'client_feedback_form_type_detailed',
    },
]

export const planTabs = {
    SUMMARY: 'summary',
    INITIATE: 'initiate',
    PLAN: 'planning',
    EXECUTE: 'execute',
    CONTROL_AND_REVIEW: 'monitor',
    CLOSE: 'finalise',
}

export const changeHistoryStatus = {
    STATUS_CREATED: 10,
    STATUS_UPDATED: 11,
    STATUS_DELETED: 12,
}

export const clientSatisfactionFeedbackTypes = {
    OTHER: 685,
}
/**
 * Available plan anchors and what tab they fit under
 */
export const planAnchorsToPlanTab = {
    'client-executed-contract': planTabs.INITIATE,
    'review-bid-proposal-info': planTabs.INITIATE,
    'written-acceptance': planTabs.INITIATE,
    'initiation-sign-offs': planTabs.INITIATE,
    'client-experience': planTabs.PLAN,
    'plan-quality': planTabs.PLAN,
    'task-plans': planTabs.PLAN,
    'risk-register': planTabs.PLAN,
    'planning-sign-offs': planTabs.PLAN,
    'client-feedback-schedule': planTabs.PLAN,
    'execute-scope-schedule-and-cost': planTabs.EXECUTE,
    'execute-client-experience': planTabs.EXECUTE,
    'execute-hse-design-and-other-risks': planTabs.EXECUTE,
    'execute-resources-and-procurement': planTabs.EXECUTE,
    'execute-other-stakeholder-interfaces': planTabs.EXECUTE,
    'execute-information-and-communication': planTabs.EXECUTE,
    'execute-quality': planTabs.EXECUTE,
    'review-project-performance': planTabs.CONTROL_AND_REVIEW,
    'monitor-sheq': planTabs.CONTROL_AND_REVIEW,
    'monitor-scope-schedule-cost': planTabs.CONTROL_AND_REVIEW,
    'monitor-manage-change': planTabs.CONTROL_AND_REVIEW,
    'client-feedback': planTabs.CONTROL_AND_REVIEW,
    'lessons-learned': planTabs.CLOSE,
    'capture-project-knowledge': planTabs.CLOSE,
    'finalise-sign-offs': planTabs.CLOSE,
    'plan-scope-schedule-and-cost': planTabs.PLAN,
    'audit-and-inspection': planTabs.PLAN,
    'budget-updates': planTabs.CONTROL_AND_REVIEW,
    'invoice-requests': planTabs.CONTROL_AND_REVIEW,
    'risk-and-opportunity': planTabs.CONTROL_AND_REVIEW,
    'work-at-risk': planTabs.INITIATE,
}

export const relevantProgrammes = {
    FUTURE_READY_RELEVANT_NOT_INCLUDED: 3601,
    FUTURE_READY_MINOR_IMPACT: 3602,
    FUTURE_READY_RELEVANT_CONTRIBUTION: 3603,
    FUTURE_READY_MAJOR_ELEMENT: 3604,
    FUTURE_READY_PRIMARY_FOCUS: 3605,
    INDIGENOUS_RELATED_DELIVERY: 3609,
    CARBON_MANAGEMENT_WSP_COMMITMENT_MEASURE_REDUCE_CARBON: 3612,
    CARBON_MANAGEMENT_CLIENT_REQUIREMENT_MEASURE_REDUCE_CARBON: 3616,
    INDIGENOUS_PROCUREMENT: 3617,
    SDG_LINKED_REVENUE: 3618,
    TAX_CREDITS_POTENTIAL_RND: 3650,
}

export const projectDeliverableTypes = {
    TYPE_DELIVERABLE: 230,
    TYPE_HOLD_POINT: 231,
    TYPE_MILESTONE: 232,
    TYPE_OTHER: 233,
}

export const sectionTypes = {
    TYPE_INITIATE: 3619,
    TYPE_PLAN: 3620,
    TYPE_EXECUTE: 3621,
    TYPE_CONTROL_AND_REVIEW: 3622,
    TYPE_CLOSE: 3623,
}

export const invoiceTypes = {
    INVOICE_TYPE_DEFAULT: 3700,
    INVOICE_TYPE_FIXED_FEE: 3701,
    INVOICE_TYPE_HOURS_AND_EXPENSES: 3702,
    INVOICE_TYPE_MIXED: 3703,
}

export const budgetUpdateTypes = {
    BUDGET_UPDATE_TYPE_COST_TO_COMPLETE: 1200,
    BUDGET_UPDATE_TYPE_VARIATION: 1201,
    BUDGET_UPDATE_TYPE_CLOSEOUT: 1202,
    BUDGET_UPDATE_TYPE_INITIAL_BUDGET: 1203,
}

export const invoiceLayouts = {
    INVOICE_LAYOUT_SIMPLE: 3720,
    INVOICE_LAYOUT_PROJECT_FEE_PERCENTAGE_COMPLETE: 3721,
    INVOICE_LAYOUT_WORK_ORDER_FEE_PERCENTAGE_COMPLETE: 3722,
    INVOICE_LAYOUT_WORK_ORDER_FEE_PERCENTAGE_COMPLETE_WORK_ORDER_ON_SUPPORTING_PAGE: 3723,
    INVOICE_LAYOUT_WORK_ORDER_FEE_PERCENTAGE_COMPLETE_PHASES_ON_SUPPORTING_PAGE: 3724,
    INVOICE_LAYOUT_LINE_DESCRIPTION_AND_LINE_AMOUNT: 3725,
}

export const invoiceRequestStatuses = {
    STATUS_DRAFT: 3760,
    STATUS_PROCESSING: 3761,
    STATUS_COMPLETE: 3762,
    STATUS_CANCELLED: 3763,
    STATUS_SUBMITTED: 3764,
}

export const workOrderStatuses = {
    STATUS_ACTIVE: 3100,
    STATUS_PARKED: 3101,
    STATUS_CLOSED: 3102,
    STATUS_TERMINATED: 3103,
}

export const workOrderExpenseStatuses = {
    STATUS_CURRENT_INVOICE_PROPOSAL: 3800,
    STATUS_PARKED: 3801,
    STATUS_READY_FOR_INVOICING: 3802,
    STATUS_CLOSED: 3803,
}

export const workOrderExpenseTypes = {
    WORK_ORDER_EXPENSE_TYPE_LAB_COSTS: 3850,
    WORK_ORDER_EXPENSE_TYPE_SUBCONTRACTORS: 3851,
    WORK_ORDER_EXPENSE_TYPE_EXPENSES: 3852,
    WORK_ORDER_EXPENSE_TYPE_HOURS: 3853,
    WORK_ORDER_EXPENSE_TYPE_UNKNOWN: 3864,
    WORK_ORDER_EXPENSE_TYPE_FIXED_FEE: 3855,
    WORK_ORDER_EXPENSE_TYPE_PHONE_N_NETWORK: 3856,
    WORK_ORDER_EXPENSE_TYPE_CATERING: 3857,
    WORK_ORDER_EXPENSE_TYPE_PARTIAL_CREDIT: 3858,
    WORK_ORDER_EXPENSE_TYPE_PARTIAL_FIXED_FEE_GST_FEE: 3859,
    WORK_ORDER_EXPENSE_TYPE_DISCOUNT: 3860,
    WORK_ORDER_EXPENSE_TYPE_ACCRUAL_ADJUSTMENT: 3861,
    WORK_ORDER_EXPENSE_TYPE_PRINTING: 3862,
    WORK_ORDER_EXPENSE_TYPE_EQUIPMENT: 3863,
    WORK_ORDER_EXPENSE_TYPE_VEHICLE_COSTS: 3864,
    WORK_ORDER_EXPENSE_TYPE_TRAVEL_N_ACCOMODATION: 3865,
}

export const projectContractTypes = {
    PLAN_CONTRACT_TYPE_MASTER: 'Master (Signed)', // the project has many contracts spreaded over many regions, and one contract has many sub projects
    PLAN_CONTRACT_TYPE_MSA: 'MSA (Signed)', // the project has one contract with many sub projects in one region
    PLAN_CONTRACT_TYPE_SINGLE: 'Contract (Signed)', // contract only has one project
    PLAN_CONTRACT_TYPE_RECHARGE: 'Recharge',
}

export const filePathTypes = {
    WRITTEN_ACCEPTANCE_TO_PROCEED: 5120,
    CLIENT_EXPERIENCE_PLAN: 5121,
    CLIENT_CONTRACT: 5122,
    CLIENT_FEEDBACK: 5123,
    CLIENT_FEEDBACK_SCHEDULE: 5124,
    EXECUTE_AS_PLANNED: 5125,
    LESSON_LEARNED: 5126,
    PROJECT_DELIVERABLE: 5127,
    PROJECT_EXECUTION_PLAN: 5128,
    PROJECT_REVIEW: 5129,
    RISK_OPPORTUNITY: 5130,
    RISK_REGISTER: 5131,
    SCOPE_CHANGE: 5132,
    TASK_ATTACHMENT: 5133,
    TASK_PLAN: 5134,
}

export const riskAndOpportunityTypes = {
    TYPE_RISK: 7000,
    TYPE_OPPORTUNITY: 7001,
}

export const riskAndOpportunityCategories = {
    CATEGORY_JV_CONTINGENCY: 6000,
    CATEGORY_PROJECT_CONTINGENCY: 6001,
    CATEGORY_MANAGEMENT_RESERVE: 6002,
    CATEGORY_SETTLEMENT: 6003,
    CATEGORY_GAIN_SHARE: 6004,
    CATEGORY_PAIN_SHARE: 6005,
    CATEGORY_LIMB_2: 6006,
    CATEGORY_LEGAL_CLAIM: 6007,
    CATEGORY_OTHER: 6008,
}

export const manuallyAssignableTaskTypes = {
    TYPE_ACTION_REQUIRED_LESSON_LEARNED: 4,
    TYPE_ACTION_REQUIRED_PROJECT_REVIEW: 5,
    TYPE_ACTION_REQUIRED_RISK_REGISTER: 6,
    TYPE_ACTION_REQUIRED_TASK_PLAN: 7,
    TYPE_ACTION_REQUIRED_SCOPE_CHANGES: 8,
    TYPE_ACTION_REQUIRED_CLIENT_FEEDBACK: 9,
    TYPE_ACTION_REQUIRED_SHEQ_INSPECTION: 13,
    TYPE_ACTION_REQUIRED_CLIENT_EXPERIENCE: 18,
    TYPE_ACTION_REQUIRED_HSE_DESIGN_AND_OTHER_RISKS: 19,
    TYPE_ACTION_REQUIRED_RESOURCES_AND_PROCUREMENT: 20,
    TYPE_ACTION_REQUIRED_OTHER_STAKEHOLDER_INTERFACES: 21,
    TYPE_ACTION_REQUIRED_INFORMATION_AND_COMMUNICATION: 22,
    TYPE_ACTION_REQUIRED_QUALITY: 23,
    TYPE_ACTION_REQUIRED_SCOPE_SCHEDULE_AND_COST: 24,
    TYPE_ACTION_REQUIRED_RISK_AND_OPPORTUNITY: 25,
}

export const riskAndOpportunityStatuses = {
    STATUS_OPEN: 6050,
    STATUS_CLOSED: 6051,
}

export const projectLocatorMessageTypes = {
    READY: 1, // Project locator is all loaded and ready to go
    SEND_EXTERNAL_PROJECT_NUMBERS: 2, // Parent application is sending through external project numbers to filter project locator on
    ADJUST_EXTERNAL_FILTERS: 3, // Project locator requesting filter change in external application
}

export const dataSourceRegionSettings = [
    { title: 'has_synced_sheq_inspections', color: 'purple-6', icon: 'sync' },
    { title: 'show_financials', color: 'gray-6', icon: 'attach_money' },
    { title: 'show_enable_sharepoint_teams', color: 'cyan-7', icon: 'share' },
    { title: 'show_resource_planner_link', color: 'light-blue-7', icon: 'integration_instructions' },
    { title: 'has_client_feedback_integration', color: 'red-8', icon: 'feedback' },
    { title: 'show_invoice_request', color: 'green-7', icon: 'receipt' },
    { title: 'show_budget_update', color: 'orange-8', icon: 'price_change' },
    { title: 'has_lessons_learned_integration', color: 'deep-orange-8', icon: 'local_library' },
    { title: 'has_external_change_orders', color: 'brown-6', icon: 'change_circle' },
    { title: 'is_synced_from_gdw', color: 'pink-6', icon: 'cloud_sync' },
]

// Should match how these are defined in App\Enums\TaskFilterPreset
export const taskFilterPresets = {
    ASSIGNED_TO_ME: 1,
    ASSIGNED_TO_MY_PROJECTS: 2,
    ASSIGNED_TO_MY_DIRECT_REPORTS: 3,
    ALL_PROJECTS: 4,
}

export const defaultApexChartOpts = {
    chart: {
        toolbar: {
            show: false,
        },
        zoom: {
            enabled: false, // Interrupts user scrolling the page
        },
    },
}
